<template>
  <div>
    <table class="asd-table">
      <TableHeading v-if="showHeading"
                    :leadingCols="leadingCols"
                    :trailingCols="[]"
                    :cols="taskPropertiesToShow"
                    :defaultSorter="defaultSorter"
                    :userSorter="userSorter"
                    :addButton="addButton"
                    :menuItems="[
                      {icon: 'fa-file-export', label: 'Exportovat jako CSV', action: exportTableData},
                    ]"
                    @sorter-changed="onSorterChanged"
      >
        <SavedFilters
          :userFilter="userFilter"
          savedFilterId="task-table"
          @filter-changed="onFilterChanged"
        ></SavedFilters>
      </TableHeading>
      <TableFilter v-if="showFilter"
                   :leadingCols="leadingCols"
                   :trailingCols="[]"
                   :cols="taskPropertiesToShow"
                   :userFilter="userFilter"
                   @filter-changed="onFilterChanged"
      ></TableFilter>
      <tbody>
        <TasksRow v-for="task in pagedTasks"
                  :key="task.id"
                  :taskId="task.id"
                  :propsToShow="taskPropertiesToShow"
                  :showProjectColumn="showProjectColumn"
                  :backProjectId="backProjectId"
        ></TasksRow>
      </tbody>
    </table>
    <Paginator v-model="currentPage"
               :numPages="numPages"
               @input="onPageChanged"
    ></Paginator>
  </div>
</template>

<script>
import _ from 'lodash'

import TasksRow from 'CORE/components/task/row'
import TableFilter from 'CORE/components/table-view/filter'
import TableHeading from 'CORE/components/table-view/heading'
import SavedFilters from 'CORE/components/table-view/saved-filters'
import Paginator from 'CORE/components/table-view/paginator'
import labels from 'CORE/utils/labels'
import ExportTableMixin from 'CORE/mixins/export-table'

import { czSorter } from 'CORE/utils/sorters'
import { normalizeFts } from 'CORE/components/data-type/comparators'

export default {
    name: 'TasksTable',
    components: {
        TasksRow,
        TableFilter,
        TableHeading,
        Paginator,
        SavedFilters,
    },
    mixins: [
        ExportTableMixin,
    ],
    props: {
        rootFilter: {
            required: false,
            default: _ => {},
            type: Object,
        },
        showHeading: {
            type: Boolean,
            default: true,
        },
        showFilter: {
            type: Boolean,
            default: true,
        },
        fixedPagination: {
            type: Boolean,
            default: false,
        },
        showProjectColumn: {
            type: Boolean,
            default: true,
        },
        projectTasks: {
            type: Boolean,
            default: false,
        },
        backProjectId: {
            type: String,
            required: false,
            default: _ => null,
        },
        taskStatus: {
            type: String,
            required: false,
            default: _ => '$all',
        },
        queryFilter: {
            type: Object,
            required: false,
            default: _ => {return {}},
        },
        querySorter: {
            type: Object,
            required: false,
            default: _ => {return {}},
        },
        queryPager: {
            type: Object,
            required: false,
            default: _ => {return {}},
        },
        addButton: {
            type: Object,
            required: false,
            default: _ => null,
        },
    },
    data() {
        return {
            userFilter: {},
            userSorter: {},
            currentPage: 0,
        }
    },
    computed: {
        defaultSorter() {
            return _.get(this.$store.getters.currentUser, 'company.settings.gui.sorter.tasks', { column: '_type', order: 'asc' })
        },
        leadingCols() {
            const cols = [null, { label: 'Type', key: '_type' }]
            if (this.showProjectColumn) cols.push({ label: 'Projekt', key: '_project' })
            return cols
        },
        filteredTasks() {
            const filter = _.merge(this.userFilter, this.rootFilter)
            const tasks = this.$store.getters.tasksBy({
                projectId: this.projectTasks ? this.$route.params.id : undefined,
                taskStatus: this.taskStatus,
            })
            if (_.size(filter) === 0) {
                return tasks
            }
            return _.filter(tasks, p => {
                let matches = 0
                _.each(filter, (filterValue, column) => {
                    if (column === '_type') {
                        const taskType = this.$store.getters.taskTypeById(_.get(p, 'taskType.id'))
                        const normalizedFilterValue = normalizeFts(filterValue)
                        const normalizedProjectName = normalizeFts(_.get(taskType, 'name'))
                        if (normalizedProjectName.search(normalizedFilterValue) >= 0) {
                            matches++
                        }
                    } else if (column === '_project') {
                        const project = this.$store.getters.getProjectById(_.get(p, 'project.id'))
                        const normalizedFilterValue = normalizeFts(filterValue)
                        const normalizedProjectName = normalizeFts(labels.project(project))
                        if (normalizedProjectName.search(normalizedFilterValue) >= 0) {
                            matches++
                        }
                    } else {
                        const normalizedFtsValue = normalizeFts(_.get(p, `propertyValues.${column}.$fts`, ''))
                        if (normalizedFtsValue.search(normalizeFts(filterValue)) >= 0) {
                            matches++
                        }
                    }
                })
                return matches === _.size(filter)
            })
        },
        sortedTasks() {
            const defaultColumn = 'system-name'
            let tasks = this.filteredTasks
            tasks = _.sortWith(tasks, (taskA, taskB) => {
                let sortingValueA; let sortingValueB
                if (_.get(this, 'userSorter.column') === '_project') {
                    sortingValueA = _.get(taskA, `project.id`)
                    sortingValueB = _.get(taskB, `project.id`)
                } else if (_.get(this, 'userSorter.column') === '_type') {
                    sortingValueA = _.get(this.$store.getters.taskTypeById(_.get(taskA, `taskType.id`)), 'name')
                    sortingValueB = _.get(this.$store.getters.taskTypeById(_.get(taskB, `taskType.id`)), 'name')
                } else {
                    sortingValueA = _.get(taskA, `propertyValues.${_.get(this, 'userSorter.column', defaultColumn)}.$srt`)
                    sortingValueB = _.get(taskB, `propertyValues.${_.get(this, 'userSorter.column', defaultColumn)}.$srt`)
                }
                return czSorter(_.get(this, 'userSorter.order', 'desc'))(sortingValueA, sortingValueB)
            })
            return tasks
        },
        pagedTasks() {
            const paging = _.get(this.$store.getters.settings, 'gui.tasksPaging', 20)
            const start = this.currentPage * paging
            const end = start + paging
            return _.slice(this.sortedTasks, start, end)
        },
        numPages() {
            return Math.ceil(_.size(this.filteredTasks) / _.get(this.$store.getters.settings, 'gui.taskPaging', 20))
        },
        taskPropertiesToShow() {
            let propertyTypes = []
            _.forEach(_.map(this.projectTasks
                ? this.$store.getters.tasksByProjectId(this.$route.params.id)
                : this.$store.getters.tasks, 'propertyValues'), obj => {
                propertyTypes = _.union(propertyTypes, _.keys(obj))
            })
            propertyTypes = _.filter(propertyTypes, p => {
                const pt = this.$store.getters.propertyTypeByKey(p)
                if (_.includes(_.get(pt, 'settings.visible', []), 'tasksTable')) {
                    return p
                }
            })
            propertyTypes = _.union(propertyTypes, _.filter(_.keys(this.userFilter), k => !_.startsWith(k, '_')))
            propertyTypes = _.sortBy(propertyTypes, p => {
                const pt = this.$store.getters.propertyTypeByKey(p)
                return _.parseInt(_.get(pt, 'settings.order', 0))
            })
            return _.defaults(propertyTypes, [])
        },
        taskPropertiesObjectsToShow() {
            const tp = {}
            if (this.taskPropertiesToShow) {
                for (const p of this.taskPropertiesToShow) {
                    _.set(tp, p, this.$store.getters.propertyTypeByKey(p))
                }
                return tp
            }
            return {}
        },
        projectTaskTypes() {
            const types = {}
            _.forEach(this.$store.getters.taskTypesByProjectId(this.$route.params.id), type => {
                _.set(types, type, this.$store.getters.taskTypeById(type))
            })
            return types
        },
        exportEntities() {
            return this.pagedTasks
        },
        exportKeys() {
            return this.taskPropertiesToShow
        },
    },
    watch: {
        rootFilter(nw) { // TODO upravit spravny merge filtru aby se nemusel resetovat takhle blbe
            if (nw === null) {
                this.userFilter = {}
            }
        },
    },
    mounted() {
        this.userFilter = this.queryFilter
        if (_.isEmpty(this.querySorter)) {
            this.userSorter = this.defaultSorter
        } else {
            this.userSorter = this.querySorter
        }
        this.currentPage = _.get(this.queryPager, 'page', 0)
    },
    methods: {
        onFilterChanged(params) {
            this.currentPage = 0
            this.$set(this, 'userFilter', _.pickBy(params, f => _.size(f) > 0))
            this.$emit('filter-changed', this.userFilter)
        },
        onSorterChanged(params) {
            this.userSorter = params
            this.$emit('sorter-changed', this.userSorter)
        },
        onPageChanged(page) {
            this.$emit('pager-changed', { page, numPages: this.numPages })
        },
    },
}
</script>
